import React, { createContext, useContext, useMemo } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { defineMessages, useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar/avatar';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import Suspense from '@atlaskit/react-ufo/suspense';

import { AgentRowAvatar } from '@confluence/agent-user-dual-avatar/entry-points/AgentRowAvatar.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { usePageContentId, usePageSpaceKey } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { UserShape } from '../ownership-models.ts';

import CollaboratorsQuery, {
	type PageOwnershipByLineWithCollaboratorsDropdownContentQuery$data,
	type PageOwnershipByLineWithCollaboratorsDropdownContentQuery as CollaboratorsQueryType,
	type PageOwnershipByLineWithCollaboratorsDropdownContentQuery$variables,
} from './__generated__/PageOwnershipByLineWithCollaboratorsDropdownContentQuery.graphql.ts';
import { getCollaboratorItems } from './getCollaboratorItems.ts';
import { getCollaboratorAgentIcon } from './getCollaboratorAgentIcon.ts';
import { getContentCollaboratorsQueryVariables } from './getContentCollaboratorsQueryVariables.ts';
import { getUserAvatarSrc } from './getUserAvatarSrc.ts';

const messages = defineMessages({
	byline: {
		id: 'content-ownership.byline.with-collaborators',
		defaultMessage:
			'By {ownerName}{otherCount, plural, =0 {} one { and 1 other} other { and # others}}',
		description:
			'Page byline showing the owner followed by the number of other human and agent-person contributors.',
	},
});

const styles = cssMap({
	avatar: {
		position: 'relative',
		flexShrink: 0,
	},
	overlappingAvatar: {
		position: 'relative',
		flexShrink: 0,
		marginInlineStart: token('space.negative.050'),
	},
});

type CollaboratorsBylineProps = {
	owner: UserShape;
	fallbackContributorCount: number;
};

// Error boundaries retain their fallback component, so read the current owner/count through context.
const BylineFallbackContext = createContext<React.JSX.Element | null>(null);
const BylineFallback = () => useContext(BylineFallbackContext);

// Mounted only for the agent-presence experiment. The preloader warms the same Relay operation.
export const CollaboratorsByline = (props: CollaboratorsBylineProps): React.JSX.Element => {
	const { cloudId } = useSessionData();
	const [contentId] = usePageContentId();
	const [spaceKey] = usePageSpaceKey();
	const fallback = useMemo(
		() => (
			<CollaboratorsBylineContent
				owner={props.owner}
				fallbackContributorCount={props.fallbackContributorCount}
			/>
		),
		[props.owner, props.fallbackContributorCount],
	);

	if (!contentId || !spaceKey || !cloudId) {
		return fallback;
	}

	const variables = getContentCollaboratorsQueryVariables({
		cloudId,
		contentId,
		spaceKey,
		shouldIncludeJobTitle: expValEquals('cc_show_byline_job_title', 'isEnabled', true),
	});

	return (
		<BylineFallbackContext.Provider value={fallback}>
			<GenericErrorBoundary
				key={`${cloudId}:${contentId}:${spaceKey}`}
				attribution={Attribution.PAGE_EXPERIENCES}
				renderOnError={BylineFallback}
			>
				<Suspense fallback={fallback} interactionName="collaborators-byline">
					<QueriedCollaboratorsByline {...props} variables={variables} />
				</Suspense>
			</GenericErrorBoundary>
		</BylineFallbackContext.Provider>
	);
};

const QueriedCollaboratorsByline = ({
	variables,
	...props
}: CollaboratorsBylineProps & {
	variables: PageOwnershipByLineWithCollaboratorsDropdownContentQuery$variables;
}): React.JSX.Element => {
	const data = useLazyLoadQuery<CollaboratorsQueryType>(CollaboratorsQuery, variables, {
		fetchPolicy: 'store-or-network',
	});

	return (
		<CollaboratorsBylineContent {...props} collaborators={data.confluence_contentCollaborators} />
	);
};

const CollaboratorsBylineContent = ({
	owner,
	fallbackContributorCount,
	collaborators,
}: CollaboratorsBylineProps & {
	collaborators?: PageOwnershipByLineWithCollaboratorsDropdownContentQuery$data['confluence_contentCollaborators'];
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const items = useMemo(
		() => getCollaboratorItems(collaborators, owner.userId),
		[collaborators, owner.userId],
	);
	const otherCount = collaborators?.nodes ? items.length : fallbackContributorCount;
	const visibleItems = useMemo(() => {
		const seenAgents = new Set<string>();
		return items
			.filter((item) => {
				if (item.kind === 'human') {
					return true;
				}
				if (seenAgents.has(item.agent.accountId)) {
					return false;
				}
				seenAgents.add(item.agent.accountId);
				return true;
			})
			.slice(0, 2);
	}, [items]);

	return (
		<Inline space="space.050" alignBlock="center">
			{/* The button's text names the collection; individual avatars are decorative. */}
			<Inline alignBlock="center" aria-hidden="true" testId="byline-collaborator-avatars">
				<Box xcss={styles.avatar}>
					<Avatar src={getUserAvatarSrc(owner)} borderColor="transparent" size="xxsmall" />
				</Box>
				{visibleItems.map((item) => (
					<Box key={item.key} xcss={styles.overlappingAvatar}>
						{item.kind === 'agent' ? (
							<AgentRowAvatar
								name={item.agent.displayName}
								agentIcon={getCollaboratorAgentIcon(item.agent.displayName)}
								avatarUrl={item.agent.avatarUrl}
								avatarSize="xxsmall"
								hasInlineSpacing={false}
							/>
						) : (
							<Avatar src={getUserAvatarSrc(item.user)} borderColor="transparent" size="xxsmall" />
						)}
					</Box>
				))}
			</Inline>
			<Text size="small" color="color.text.subtle" maxLines={1}>
				{formatMessage(messages.byline, { ownerName: owner.fullName, otherCount })}
			</Text>
		</Inline>
	);
};
